import { GenericError } from '@epihub/core/src/errors';
import type { TMigrationKeySplit } from './types';
import { findShortestMigrationPath } from './findShortestMigrationPath';

type TVersionMapSync<Versions extends Record<string, any>> = {
  [VersionKey in keyof Versions]: (data: Versions[VersionKey]) => boolean;
};

type TMigrationsSync<Versions extends Record<string, any>> = {
  [K in `${keyof Versions & string}${TMigrationKeySplit}${keyof Versions & string}`]: K extends
    `${infer PreviousKey}${TMigrationKeySplit}${infer TargetKey}`
    ? (previous: Versions[PreviousKey]) => Versions[TargetKey]
    : never;
};

export type TVersioner<Versions extends Record<string, any>> = ReturnType<
  typeof versionerSync<Versions>
>;

export function versionerSync<Versions extends Record<string, any>>(
  typename: string,
  p: {
    versionMap: TVersionMapSync<Versions>;
    migrations: Partial<TMigrationsSync<Versions>>;
  },
) {
  const identifyVersion = buildVersionIdentifier(typename, p);

  const versioner = function<TargetVersionKey extends keyof Versions>(
    targetVersionKey: TargetVersionKey,
    anyVersion: Versions[string],
  ): Versions[TargetVersionKey] {
    const inputVersionKey = identifyVersion(anyVersion);

    if (inputVersionKey === targetVersionKey) {
      // No migration needed
      return anyVersion as Versions[TargetVersionKey];
    }

    // Find the shortest migration path from inputVersionKey to targetVersionKey
    const migrationPath = findShortestMigrationPath({
      startKey: inputVersionKey as string,
      targetKey: targetVersionKey as string,
      migrationKeys: Object.keys(p.migrations ?? {}) as TMigrationKeySplit[],
    });

    if (!migrationPath || migrationPath.length <= 0) {
      throw new GenericError({
        scope: 'versionerSync',
        message: `No migration path found from ${inputVersionKey as string} to ${targetVersionKey as string}`,
      });
    }

    // Apply each migration in the path
    let currentVersion: any = anyVersion;
    for (const step of migrationPath) {
      const migrationKey = step as keyof typeof p.migrations;
      const migrationFn = p.migrations[migrationKey];

      if (!migrationFn) {
        throw new GenericError({
          scope: 'versionerSync',
          message: `Missing migration function for ${migrationKey}`,
        });
      }

      currentVersion = migrationFn(currentVersion);
    }

    return currentVersion as Versions[TargetVersionKey];
  };

  versioner.identifyVersion = identifyVersion;
  return versioner;
}

function buildVersionIdentifier<Versions extends Record<string, any>>(
  typename: string,
  p: {
    versionMap: TVersionMapSync<Versions>;
  },
) {
  const versionMap = Object.entries(p.versionMap);

  return function(anyVersion: Versions[string]): keyof Versions {
    const identifiedVersions = versionMap
      .filter(([, isVersion]) => isVersion(anyVersion));

    if (identifiedVersions.length <= 0) {
      throw new GenericError({
        scope: 'versionerSync',
        message: `typename(${typename}) had no version matches`,
      });
    }

    if (identifiedVersions.length > 1) {
      const identifiedKeys = identifiedVersions.map((iv) => iv[0]);
      throw new GenericError({
        scope: 'versionerSync',
        message: `object(${typename}) had multiple version matches ${identifiedKeys}`,
      });
    }

    // Exactly 1 version match
    const [inputVersionKey] = identifiedVersions[0];

    return inputVersionKey as keyof Versions;
  };
}
